import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  AttachmentIcon,
  CancelX,
  DeleteIcon,
  DropdownCloseIcon,
  TrashIcon,
} from "../Icons";
import { useAuth } from "../../hooks/useAuth";
import Datetime from "react-datetime";
import { currenciesOptions } from "../../utils/currencies";
import { useStoreState } from "easy-peasy";
import Select from "react-select";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import { isEmpty } from "lodash";
import { useCurrencies } from "../../utils/hooks";

import React, { useState, useRef } from "react";
import { ProgressBar, Alert } from "react-bootstrap"; // Assuming you're using react-bootstrap
import { waitFor } from "../../utils/helpers";

function Attachments({ files = [], setFiles }) {
  const { backendUrl } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [retryingFiles, setRetryingFiles] = useState(new Set());

  // Store original files for retry functionality
  const originalFilesRef = useRef(new Map());

  const uploadSingleFile = async (file, fileKey) => {
    const formData = new FormData();
    formData.append("file", file);

    // Update progress for this file
    const updateProgress = (status, progress = 0, error = null) => {
      setUploadProgress(prev => ({
        ...prev,
        [fileKey]: { status, progress, error, fileName: file.name },
      }));
    };

    try {
      updateProgress("uploading", 0);

      const response = await fetch(
        `${backendUrl}/api/job/po-attachments/upload`,
        {
          method: "POST",
          body: formData,
          // Add auth headers if needed
          headers: {
            // 'Authorization': `Bearer ${token}`, // Uncomment and adjust if you need auth
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      updateProgress("uploading", 90);
      // await waitFor(400000);
      const uploadedFile = await response.json();
      updateProgress("completed", 100);

      // Return file object with expected structure
      return {
        fileId: uploadedFile.fileId,
        name: uploadedFile.originalName || file.name,
        systemFilename: uploadedFile.systemFilename,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error(`Upload error for ${file.name}:`, error);
      updateProgress("failed", 0, error.message);

      // Store original file for potential retry
      originalFilesRef.current.set(fileKey, file);

      // Return file with error state
      return {
        fileKey, // Add fileKey to identify this file for retry
        name: file.name,
        error: error.message,
        size: file.size,
        type: file.type,
        failed: true,
      };
    }
  };

  const uploadFiles = async selectedFiles => {
    setUploading(true);
    setGlobalError("");
    setUploadProgress({});

    try {
      const fileArray = Array.from(selectedFiles);
      const uploadPromises = fileArray.map((file, index) => {
        const fileKey = `upload_${Date.now()}_${index}`;
        return uploadSingleFile(file, fileKey);
      });

      const uploadedFiles = await Promise.allSettled(uploadPromises);

      // Process results
      const successfulUploads = [];
      const failedUploads = [];

      uploadedFiles.forEach((result, index) => {
        if (result.status === "fulfilled") {
          if (result.value.failed) {
            failedUploads.push(result.value);
          } else {
            successfulUploads.push(result.value);
          }
        } else {
          const fileKey = `upload_${Date.now()}_${index}`;
          const failedFile = {
            fileKey,
            name: fileArray[index].name,
            error: result.reason?.message || "Unknown error",
            size: fileArray[index].size,
            type: fileArray[index].type,
            failed: true,
          };
          failedUploads.push(failedFile);
          // Store original file for retry
          originalFilesRef.current.set(fileKey, fileArray[index]);
        }
      });

      // Update files state with both successful and failed uploads
      setFiles([...files, ...successfulUploads, ...failedUploads]);

      // Show summary if there were failures
      if (failedUploads.length > 0) {
        setGlobalError(
          `${
            failedUploads.length
          } file(s) failed to upload. Click the retry button to try again.`
        );
      }
    } catch (error) {
      console.error("Batch upload error:", error);
      setGlobalError("Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
      // Clear completed progress after a delay
      setTimeout(() => {
        setUploadProgress(prev => {
          const filtered = {};
          Object.entries(prev).forEach(([key, value]) => {
            if (value.status !== "completed") {
              filtered[key] = value;
            }
          });
          return filtered;
        });
      }, 3000);
    }
  };

  const retryFailedFile = async (failedFile, index) => {
    const { fileKey } = failedFile;
    const originalFile = originalFilesRef.current.get(fileKey);

    if (!originalFile) {
      alert("Original file not found. Please re-select the file.");
      return;
    }

    setRetryingFiles(prev => new Set([...prev, fileKey]));

    try {
      // Generate new fileKey for retry
      const newFileKey = `retry_${Date.now()}_${Math.random()}`;

      const result = await uploadSingleFile(originalFile, newFileKey);

      if (result.failed) {
        // Update the existing failed file with new error
        const updatedFiles = [...files];
        updatedFiles[index] = { ...result, fileKey: newFileKey };
        setFiles(updatedFiles);

        // Update the original file reference
        originalFilesRef.current.set(newFileKey, originalFile);
        originalFilesRef.current.delete(fileKey);
      } else {
        // Replace failed file with successful one
        const updatedFiles = [...files];
        updatedFiles[index] = result;
        setFiles(updatedFiles);

        // Clean up references
        originalFilesRef.current.delete(fileKey);

        // Clear any global error if this was the last failed file
        const remainingFailedFiles = updatedFiles.filter(f => f.failed);
        if (remainingFailedFiles.length === 0) {
          setGlobalError("");
        }
      }
    } catch (error) {
      console.error("Retry error:", error);
      alert(`Retry failed: ${error.message}`);
    } finally {
      setRetryingFiles(prev => {
        const updated = new Set(prev);
        updated.delete(fileKey);
        return updated;
      });
    }
  };

  const deleteFile = async (fileToDelete, index) => {
    // If file has fileId, delete from server
    if (fileToDelete.fileId) {
      try {
        const response = await fetch(
          `${backendUrl}/api/job/po-attachments/${fileToDelete.fileId}`,
          {
            method: "DELETE",
            // Add auth headers if needed
            headers: {
              // 'Authorization': `Bearer ${token}`, // Uncomment and adjust if you need auth
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Delete failed: ${response.status} ${response.statusText}`
          );
        }
      } catch (error) {
        console.error("Delete error:", error);
        alert(`Failed to delete file: ${error.message}`);
        return; // Don't remove from local state if server delete failed
      }
    }

    // Clean up original file reference if it exists
    if (fileToDelete.fileKey) {
      originalFilesRef.current.delete(fileToDelete.fileKey);
    }

    // Remove from local state
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleFileSelect = e => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      uploadFiles(selectedFiles);
    }
    // Clear the input so the same file can be selected again if needed
    e.target.value = "";
  };

  const clearGlobalError = () => setGlobalError("");

  // Get active upload progress
  const activeUploads = Object.values(uploadProgress);
  const hasActiveUploads = activeUploads.some(p => p.status === "uploading");

  return (
    <div>
      {/* Global Error Alert */}
      {globalError && (
        <Alert
          variant="warning"
          dismissible
          onClose={clearGlobalError}
          className="mb-0"
        >
          {globalError}
        </Alert>
      )}

      {/* Upload Progress */}
      {hasActiveUploads && (
        <div className="mb-3">
          <h6 className="h6">Uploading Files:</h6>
          {activeUploads
            .filter(p => p.status === "uploading")
            .map((progress, idx) => (
              <div key={idx} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small>{progress.fileName}</small>
                  <small>{progress.progress}%</small>
                </div>
                <ProgressBar
                  now={progress.progress}
                  variant={progress.error ? "danger" : "primary"}
                  style={{ height: "6px" }}
                />
              </div>
            ))}
        </div>
      )}

      {/* File List */}
      <div>
        {files?.map((el, index) => (
          <div
            key={el.fileKey || index}
            className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
          >
            <div className="d-flex align-items-center flex-grow-1">
              {el?.fileId ? (
                <a
                  href={`${backendUrl}/api/job/po-attachments/${
                    el.systemFilename
                  }`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary me-2 text-decoration-none"
                >
                  {el?.name}
                </a>
              ) : (
                <span className="me-2">
                  {el?.name}
                  {el?.error && (
                    <div>
                      <small className="text-danger">Error: {el.error}</small>
                    </div>
                  )}
                </span>
              )}
            </div>

            <div className="d-flex align-items-center">
              {/* Status indicator */}
              {el?.failed && (
                <span className="badge bg-danger me-2">Failed</span>
              )}

              {/* Manual retry button for failed uploads */}
              {el?.failed && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => retryFailedFile(el, index)}
                  disabled={retryingFiles.has(el.fileKey) || uploading}
                  className="me-2"
                  title="Retry upload"
                >
                  {retryingFiles.has(el.fileKey) ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Retrying...
                    </>
                  ) : (
                    <>↻ Retry</>
                  )}
                </Button>
              )}

              {/* Delete button */}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => deleteFile(el, index)}
                disabled={uploading || retryingFiles.has(el.fileKey)}
                title="Delete file"
              >
                ✕
              </Button>
            </div>
          </div>
        ))}
        {files.length > 0 && <hr className="my-3" />}
      </div>

      {/* File Upload Button */}
      <Form.Label className="btn btn-light-blue border">
        <span>{uploading ? "Uploading..." : "Add Files"}</span>
        <Form.Control
          type="file"
          className="d-none"
          multiple
          onChange={handleFileSelect}
          disabled={uploading}
        />
      </Form.Label>

      {/* Upload status */}
      {uploading && (
        <div className="mt-2">
          <small className="text-muted">Uploading files, please wait...</small>
        </div>
      )}
    </div>
  );
}

const JobPO = ({ formik }) => {
  const generalSettings = useStoreState(state => state.generalSettings);
  const {
    deploymentCurrencies: currenciesOptions,
    getCurrencySymbol,
  } = useCurrencies();

  const addPO = () => {
    formik.setFieldValue("jobPOs", [
      ...formik.values.jobPOs,
      { poNumber: "", poTitle: "", poDate: new Date() },
    ]);
  };

  const removePO = index => {
    const newPos = formik.values.jobPOs.filter((_, i) => i !== index);
    formik.setFieldValue("jobPOs", newPos);
  };

  return (
    <Card>
      <Card.Header>
        <h4 className="h6 my-3">
          <b> Purchase Order</b>
        </h4>
      </Card.Header>
      <Card.Body className="">
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <FieldArray name="jobPOs">
              {() => (
                <>
                  <div className="">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>S/N</th>
                          <th>PO Number</th>
                          <th style={{ width: "40%" }}>Title</th>
                          <th>Date</th>
                          <th>{/*Actions */}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.jobPOs.map((po, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <Form.Control
                                type="text"
                                name={`jobPOs[${index}].poNumber`}
                                value={formik.values.jobPOs[index].poNumber}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={
                                  formik.touched.jobPOs?.[index]?.poNumber &&
                                  formik.errors.jobPOs?.[index]?.poNumber
                                }
                                placeholder="Enter PO Number"
                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.jobPOs?.[index]?.poNumber}
                              </Form.Control.Feedback>
                            </td>

                            <td style={{ width: "50%" }}>
                              <div>
                                <Form.Control
                                  type="text"
                                  name={`jobPOs[${index}].poTitle`}
                                  value={formik.values.jobPOs[index].poTitle}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.jobPOs?.[index]?.poTitle &&
                                    formik.errors.jobPOs?.[index]?.poTitle
                                  }
                                  as={"textarea"}
                                  rows={2}
                                  placeholder="Enter PO Title"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.jobPOs?.[index]?.poTitle}
                                </Form.Control.Feedback>
                              </div>

                              <Form.Group className="text-nowrap my-3">
                                <Form.Label className="mb-0">Amount</Form.Label>
                                <div className="d-flex align-items-center gap-2 mt-2">
                                  <Form.Group className="text-nowrap">
                                    <Select
                                      classNamePrefix="form-select"
                                      placeholder="Select Currency"
                                      isSearchable={true}
                                      options={currenciesOptions}
                                      value={currenciesOptions.find(
                                        el =>
                                          el.value ===
                                          formik.values.jobPOs[index]?.currency
                                      )}
                                      onChange={selected => {
                                        formik.setFieldValue(
                                          `jobPOs.[${index}].currency`,
                                          selected?.value
                                        );
                                        formik.setFieldValue(
                                          `jobPOs.[${index}].conversionAmount`,
                                          Number(selected?.conversionAmount)
                                        );
                                      }}
                                    />
                                  </Form.Group>

                                  {!isEmpty(
                                    formik.values.jobPOs[index]?.currency
                                  ) &&
                                  formik.values.jobPOs[index]?.currency !=
                                    generalSettings?.prevailingCurrency ? (
                                    <Form.Group style={{ width: "300px" }}>
                                      <CurrencyCustomInput
                                        currencySymbol={"Ex."}
                                        name={`jobPOs.[${index}].conversionAmount`}
                                        value={
                                          formik.values.jobPOs[index]
                                            ?.conversionAmount
                                        }
                                        onValueChange={(value, name) => {
                                          formik.setFieldValue(name, value);
                                        }}
                                        placeholder="0.00"
                                      />
                                    </Form.Group>
                                  ) : null}

                                  <CurrencyCustomInput
                                    currencySymbol={getCurrencySymbol(
                                      formik.values.jobPOs?.[index]?.currency ||
                                        generalSettings?.prevailingCurrency
                                    )}
                                    name={`jobPOs[${index}].poAmount`}
                                    value={
                                      formik.values.jobPOs?.[index]?.poAmount
                                    }
                                    onValueChange={(value, name) => {
                                      formik.setFieldValue(name, value);
                                    }}
                                    placeholder="0.00"
                                  />
                                </div>{" "}
                              </Form.Group>

                              <Form.Group className="text-nowrap my-3">
                                <Form.Label className="mb-2">
                                  Attachments
                                </Form.Label>

                                <Form.Group>
                                  <Attachments
                                    files={
                                      formik.values.jobPOs[index]?.attachments
                                    }
                                    setFiles={files => {
                                      formik.setFieldValue(
                                        `jobPOs[${index}].attachments`,
                                        files
                                      );
                                    }}
                                  />
                                </Form.Group>
                              </Form.Group>
                            </td>
                            <td>
                              <Datetime
                                timeFormat={false}
                                closeOnSelect={true}
                                closeOnClickOutside={true}
                                dateFormat="MMM DD, YYYY"
                                inputProps={{
                                  className: `date-input form-control ${
                                    formik.touched.jobPOs?.[index]?.poDate &&
                                    !!formik.errors.jobPOs?.[index]?.poDate
                                      ? "is-invalid"
                                      : ""
                                  }`,
                                  placeholder: "Select date",
                                  readOnly: true,
                                }}
                                value={formik.values.jobPOs[index]?.poDate}
                                onChange={date => {
                                  formik.setFieldValue(
                                    `jobPOs[${index}].poDate`,
                                    date,
                                    true
                                  );
                                }}
                                onBlur={() =>
                                  formik.setFieldTouched(
                                    `jobPOs[${index}].poDate`,
                                    true
                                  )
                                }
                              />
                            </td>
                            <td>
                              {formik.values.jobPOs.length > 1 && (
                                <Button
                                  variant=""
                                  className="text-danger"
                                  size="sm"
                                  onClick={() => removePO(index)}
                                >
                                  <DeleteIcon />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <Row className="mb-3">
                    <Col>
                      <Button variant="outline-primary" onClick={addPO}>
                        + New Line
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
            </FieldArray>

            <hr />

            {/* <Form.Group className="form-mb align-items-center mb-3">
              <Form.Label>PO Attachments</Form.Label>
              <Attachments
                files={formik.values.poAttachments}
                setFiles={files => formik.setFieldValue("poAttachments", files)}
              />
            </Form.Group> */}
          </Form>
        </FormikProvider>
      </Card.Body>
    </Card>
  );
};

export default JobPO;
